import React, {
  useImperativeHandle,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';

import { useGroupContext } from './context';
import type { PanelImperativeHandle, PanelProps, SizeValue } from './types';
import { useId } from './use-id';

const parseSizeValue = (
  value: SizeValue | undefined,
  defaultValue: number,
  groupSize: number,
): number => {
  if (value === undefined) return defaultValue;
  if (typeof value === 'number') {
    // If number is > 100, assume pixels, otherwise percentage
    return value > 100 ? (value / groupSize) * 100 : value;
  }

  const str = String(value).trim();
  if (str.endsWith('%')) {
    return parseFloat(str) || defaultValue;
  }
  if (str.endsWith('px')) {
    const pixels = parseFloat(str);
    return (pixels / groupSize) * 100;
  }
  if (str.endsWith('em') || str.endsWith('rem')) {
    const emValue = parseFloat(str);
    const pixels = emValue * 16; // Approximate
    return (pixels / groupSize) * 100;
  }
  if (str.endsWith('vh') || str.endsWith('vw')) {
    const vhValue = parseFloat(str);
    const pixels = (vhValue / 100) * (window.innerHeight || window.innerWidth);
    return (pixels / groupSize) * 100;
  }

  // Assume percentage
  return parseFloat(str) || defaultValue;
};

export const Panel = React.forwardRef<PanelImperativeHandle, PanelProps>(
  (
    {
      children,
      className,
      collapsedSize = 0,
      collapsible = false,
      defaultSize,
      elementRef,
      id: idProp,
      maxSize = 100,
      minSize = 0,
      onResize,
      panelRef,
      style,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const panelId = idProp ?? generatedId;
    const internalRef = useRef<HTMLDivElement>(null);
    const {
      registerPanel,
      unregisterPanel,
      getPanelSize,
      collapsePanel,
      expandPanel,
      resizePanel,
      updatePanelSize,
      orientation,
      groupElementRef,
    } = useGroupContext();

    // Calculate initial size
    const getGroupSize = useCallback(() => {
      if (groupElementRef.current) {
        const rect = groupElementRef.current.getBoundingClientRect();
        return orientation === 'horizontal' ? rect.width : rect.height;
      }
      return 1000; // Default estimate
    }, [groupElementRef, orientation]);

    const initialSize = useMemo(() => {
      if (defaultSize !== undefined) {
        return parseSizeValue(defaultSize, 0, getGroupSize());
      }
      return 0;
    }, [defaultSize, getGroupSize]);

    const minSizePercent = useMemo(
      () => parseSizeValue(minSize, 0, getGroupSize()),
      [minSize, getGroupSize],
    );
    const maxSizePercent = useMemo(
      () => parseSizeValue(maxSize, 100, getGroupSize()),
      [maxSize, getGroupSize],
    );
    const collapsedSizePercent = useMemo(
      () => parseSizeValue(collapsedSize, 0, getGroupSize()),
      [collapsedSize, getGroupSize],
    );

    // Register panel on mount
    useEffect(() => {
      if (internalRef.current) {
        registerPanel(String(panelId), {
          id: String(panelId),
          element: internalRef.current,
          minSize: minSizePercent,
          maxSize: maxSizePercent,
          currentSize: initialSize,
          collapsedSize: collapsedSizePercent,
          collapsible,
          isCollapsed: false,
          previousSize: initialSize,
        });
      }

      return () => {
        unregisterPanel(String(panelId));
      };
    }, [
      panelId,
      registerPanel,
      unregisterPanel,
      initialSize,
      minSizePercent,
      maxSizePercent,
      collapsedSizePercent,
      collapsible,
    ]);

    // Set flex styles after mount to ensure Group is measured
    // Also update element reference in panel state
    useLayoutEffect(() => {
      if (!internalRef.current) return;

      // Update element reference in panel state
      registerPanel(String(panelId), {
        element: internalRef.current,
      });

      // Recalculate size based on actual Group container size
      const groupSize = getGroupSize();
      const defaultSizeStr = typeof defaultSize === 'string' ? defaultSize.trim() : '';
      const isPixelDefault = defaultSizeStr.endsWith('px');

      let recalculatedSize: number;
      if (defaultSize !== undefined && isPixelDefault) {
        internalRef.current.style.flexBasis = defaultSizeStr;
        internalRef.current.style.flexShrink = '0';
        internalRef.current.style.flexGrow = '0';
        recalculatedSize =
          groupSize > 0 && groupElementRef.current ? parseSizeValue(defaultSize, 0, groupSize) : 0;
        if (recalculatedSize > 0) {
          updatePanelSize(String(panelId), recalculatedSize);
        }
      } else {
        recalculatedSize =
          defaultSize !== undefined ? parseSizeValue(defaultSize, 0, groupSize) : initialSize;
        if (recalculatedSize !== initialSize && recalculatedSize > 0) {
          updatePanelSize(String(panelId), recalculatedSize);
        }
        if (recalculatedSize > 0) {
          internalRef.current.style.flexBasis = `${recalculatedSize}%`;
          internalRef.current.style.flexShrink = '0';
          internalRef.current.style.flexGrow = '0';
        }
      }

      if (recalculatedSize <= 0 && !isPixelDefault) {
        // When size is 0 (defaultSize undefined), allow grow and shrink
        internalRef.current.style.flexBasis = 'auto';
        internalRef.current.style.flexShrink = '1';
        internalRef.current.style.flexGrow = '1';
      }
      internalRef.current.style.minWidth = '0';
      internalRef.current.style.minHeight = '0';
      // Use overflow: visible by default to allow tooltips to escape
      // If overflow is explicitly set in style prop, it will override this
      if (style?.overflow === undefined) {
        internalRef.current.style.overflow = 'visible';
      }
    }, [
      defaultSize,
      initialSize,
      getGroupSize,
      groupElementRef,
      panelId,
      style?.overflow,
      updatePanelSize,
      registerPanel,
    ]);

    // Expose imperative handle
    useImperativeHandle(
      ref || panelRef,
      (): PanelImperativeHandle => ({
        collapse: () => {
          collapsePanel(String(panelId));
        },
        expand: () => {
          expandPanel(String(panelId));
        },
        getSize: () => {
          const size = getPanelSize(String(panelId));
          return size ?? { asPercentage: 0, inPixels: 0 };
        },
        isCollapsed: () => {
          // TODO: Track collapsed state
          return false;
        },
        resize: (size: SizeValue) => {
          resizePanel(String(panelId), size);
        },
      }),
      [panelId, collapsePanel, expandPanel, getPanelSize, resizePanel],
    );

    // Sync elementRef
    useEffect(() => {
      if (elementRef) {
        if (typeof elementRef === 'function') {
          elementRef(internalRef.current);
        } else if (elementRef && typeof elementRef === 'object' && 'current' in elementRef) {
          // Type assertion needed because Ref<T> can be RefObject (read-only) or MutableRefObject (writable)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (elementRef as any).current = internalRef.current;
        }
      }
    }, [elementRef]);

    // Handle resize callback
    useEffect(() => {
      if (onResize && internalRef.current) {
        const size = getPanelSize(String(panelId));
        if (size) {
          onResize(size, panelId);
        }
      }
    }, [panelId, onResize, getPanelSize]);

    return (
      <div
        ref={internalRef}
        id={String(panelId)}
        data-panel
        data-testid={String(panelId)}
        className={className}
        style={style}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Panel.displayName = 'Panel';
